import React, { useEffect, useMemo, useState } from 'react';
import axios from '../../axiosConfig';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Modal } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { FaUserFriends, FaSync, FaSearch, FaExpand, FaCompress, FaHandHoldingUsd, FaClipboardList } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import PaginationTable from '../../components/PaginationTable';
import { useListadoRecordado } from '../../components/useListadoRecordado';
import { useLoading } from '../../components/LoadingContext';
import estadosMunicipios from '../../components/estadosMunicipios.json';
import { formatoNumero } from './beneficiariosUtils';
import './Beneficiarios.css';

// "Beneficiarios sin datos": histórico de casos registrados de forma agregada
// y sus indicadores. Todos los totales están ponderados por número de
// beneficiarios, así que se pueden sumar a los del Cálculo de Indicadores.

const FILTROS_VACIOS = {
  nombre_uc: '', cod_uc: '',
  centro_id: '', cohorte: '', periodo_id: '', procedencia_id: '',
  estado_id: '', municipio: '', grupo_prioritario_id: '', grupo: '',
  es_patrocinado: '', realiza_aporte: '', patrocinante_id: '',
  fecha_mayor: '', fecha_menor: '',
};

const COLORES = ['#185da7', '#58c765', '#ffc658', '#ff7300', '#00C49F', '#8884d8', '#fc53c4', '#999'];

const activo = (v) => (v !== null && v !== undefined && String(v).trim() !== '' ? 'is-active-filter' : '');

const GraficoBarras = ({ titulo, datos, clave = 'nombre' }) => (
  <div className="chart-box" style={{ flex: '1 1 0', minWidth: 0 }}>
    <h4 style={{ fontSize: '1.1rem' }}>{titulo}</h4>
    {datos.length === 0 ? (
      <div className="text-muted small">Sin datos para el filtro aplicado.</div>
    ) : (
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={datos} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={clave} angle={-25} textAnchor="end" interval={0} tick={{ fontSize: 11 }} />
          <YAxis allowDecimals={false} />
          <Tooltip formatter={(v) => formatoNumero(v)} />
          <Bar dataKey="cantidad" name="Beneficiarios">
            {datos.map((_, i) => <Cell key={i} fill={COLORES[i % COLORES.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    )}
  </div>
);

const ShowPromocion = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [datos, setDatos] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [opciones, setOpciones] = useState({
    centro: [], periodo: [], cohorte: [], procedencia: [], grupo_prioritario: [], grupo: [], patrocinante: [],
  });
  // Página y filtros se recuerdan: al volver de Editar el listado queda donde estaba.
  const {
    filters: filtros, setFilters: setFiltros, currentPage: pagina, setCurrentPage: setPagina,
  } = useListadoRecordado('promocion', FILTROS_VACIOS);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [soloTabla, setSoloTabla] = useState(false);
  const [recargando, setRecargando] = useState(false);
  const [aEliminar, setAEliminar] = useState(null);
  const [confirmarExportar, setConfirmarExportar] = useState(false);

  const cargar = async (paginaPedida = 1, filtrosActuales = filtros) => {
    const numPagina = Number.isInteger(paginaPedida) && paginaPedida > 0 ? paginaPedida : 1;
    setRecargando(true);
    try {
      const { data } = await axios.get('/promociones-estadisticas', {
        headers,
        params: { ...filtrosActuales, page: numPagina },
      });
      setDatos(Array.isArray(data.datos?.data) ? data.datos.data : []);
      setTotalPaginas(data.datos?.last_page || 1);
      setPagina(numPagina);
      setEstadisticas(data.estadisticas || null);
    } catch (error) {
      console.error('Error cargando beneficiarios sin datos:', error);
      toast.error('No se pudieron cargar los registros');
      setDatos([]);
    } finally {
      setRecargando(false);
    }
  };

  useEffect(() => {
    const inicio = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get('/filter-promocion', { headers });
        setOpciones(data);
      } catch (error) {
        console.error('Error cargando filtros:', error);
      }
      await cargar(pagina);
      setLoading(false);
    };
    inicio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cambiarFiltro = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({ ...prev, [name]: value, ...(name === 'estado_id' ? { municipio: '' } : {}) }));
  };

  const limpiarFiltros = () => {
    setFiltros(FILTROS_VACIOS);
    cargar(1, FILTROS_VACIOS);
  };

  const municipios = useMemo(() => {
    const estado = estadosMunicipios.find((e) => e.id_estado === parseInt(filtros.estado_id, 10));
    return estado ? estado.municipios : [];
  }, [filtros.estado_id]);

  const eliminar = async () => {
    if (!aEliminar) return;
    try {
      await axios.delete(`/promocion/${aEliminar.id}`, { headers });
      toast.success('Registro eliminado');
      setAEliminar(null);
      cargar(pagina);
    } catch (error) {
      toast.error('No se pudo eliminar el registro');
    }
  };

  const exportar = async () => {
    setConfirmarExportar(false);
    try {
      const { data } = await axios.get('/promociones-print', { headers, params: filtros });
      if (!data.datos?.length) {
        toast.info('No hay registros para exportar con este filtro');
        return;
      }

      const e = data.estadisticas;
      const resumen = [
        { Indicador: 'Registros', Valor: e.totalRegistros },
        { Indicador: 'Total de beneficiarios', Valor: e.totalBeneficiarios },
        { Indicador: 'Masculino', Valor: e.totalMasculino, Porcentaje: `${e.porcentajesGenero.masculino}%` },
        { Indicador: 'Femenino', Valor: e.totalFemenino, Porcentaje: `${e.porcentajesGenero.femenino}%` },
        ...e.porGrupoEtario.map((g) => ({ Indicador: `Edad: ${g.descripcion}`, Valor: g.cantidad, Porcentaje: `${g.porcentaje}%` })),
        ...e.porProcedencia.map((g) => ({ Indicador: `Procedencia: ${g.nombre}`, Valor: g.cantidad, Porcentaje: `${g.porcentaje}%` })),
        ...e.porEstado.map((g) => ({ Indicador: `Estado: ${g.nombre}`, Valor: g.cantidad, Porcentaje: `${g.porcentaje}%` })),
        ...e.porMunicipio.map((g) => ({ Indicador: `Municipio: ${g.nombre}`, Valor: g.cantidad, Porcentaje: `${g.porcentaje}%` })),
        ...e.porCentro.map((g) => ({ Indicador: `Centro: ${g.nombre}`, Valor: g.cantidad, Porcentaje: `${g.porcentaje}%` })),
        ...e.porCohorte.map((g) => ({ Indicador: `Cohorte: ${g.nombre}`, Valor: g.cantidad, Porcentaje: `${g.porcentaje}%` })),
        ...e.porPeriodo.map((g) => ({ Indicador: `Periodo: ${g.nombre}`, Valor: g.cantidad, Porcentaje: `${g.porcentaje}%` })),
        { Indicador: 'Beneficiarios patrocinados', Valor: e.patrocinados.beneficiarios },
        { Indicador: 'Beneficiarios que realizan aporte', Valor: e.aportantes?.beneficiarios ?? 0 },
        { Indicador: 'Monto total asociado (USD)', Valor: e.montos.totalUsd },
      ];

      const libro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(libro, XLSX.utils.json_to_sheet(data.datos), 'Registros');
      XLSX.utils.book_append_sheet(libro, XLSX.utils.json_to_sheet(resumen), 'Indicadores');
      const buffer = XLSX.write(libro, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'beneficiarios_sin_datos.xlsx');
    } catch (error) {
      console.error('Error exportando:', error);
      toast.error('No se pudo exportar');
    }
  };

  const e = estadisticas;
  const generoPie = e ? [
    { name: 'Masculino', value: e.totalMasculino },
    { name: 'Femenino', value: e.totalFemenino },
  ] : [];

  // Programa primero y con su nombre (no el código); ya no hay "Caso" (26/09/2026).
  const columnas = ['Programa', 'Fecha', 'Total', 'M', 'F', 'Edades', 'Procedencia', 'Estado', 'Municipio',
    'Centro', 'Cohorte', 'Periodo', 'Aporta', 'Patrocinado', 'Monto', 'Acciones'];

  const fila = (r) => (
    <tr key={r.id}>
      <td><strong>{r.curso?.descripcion || '—'}</strong></td>
      <td className="celda-compacta">{String(r.fecha_promocion || '').slice(0, 10)}</td>
      <td className="text-end fw-bold">{formatoNumero(r.cantidad_total)}</td>
      <td className="text-end">{formatoNumero(r.cantidad_masculino)}</td>
      <td className="text-end">{formatoNumero(r.cantidad_femenino)}</td>
      <td>
        <ul className="lista-edades">
          {[...(r.distribucion_edades || [])]
            .sort((a, b) => (a.grupo_etario?.orden ?? 0) - (b.grupo_etario?.orden ?? 0))
            .map((d) => (
              <li key={d.id}>{d.grupo_etario?.descripcion.replace(' años', '')}: <strong>{formatoNumero(d.cantidad)}</strong></li>
            ))}
        </ul>
      </td>
      <td>{r.procedencia?.descripcion}</td>
      <td>{r.estado?.descripcion}</td>
      <td>{r.municipio}</td>
      <td>{r.centro?.descripcion}</td>
      <td>{r.cohorte}</td>
      <td>{r.periodo?.descripcion}</td>
      <td>{r.realiza_aporte ? 'Sí' : 'No'}</td>
      <td>{r.es_patrocinado ? 'Sí' : 'No'}</td>
      <td className="text-end">{r.monto != null ? `${formatoNumero(r.monto, 2)} ${r.tipo_moneda || ''}` : '—'}</td>
      <td>
        <div className="d-flex gap-1">
          <Button variant="btn btn-info" size="sm" title="Ver" onClick={() => navigate(`/promocion/${r.id}`)}>
            <i className="bi bi-eye-fill"></i>
          </Button>
          <Button variant="btn btn-warning" size="sm" title="Editar" onClick={() => navigate(`/promocion/${r.id}/edit`)}>
            <i className="bi bi-pencil-fill"></i>
          </Button>
          <Button variant="btn btn-danger" size="sm" title="Eliminar" onClick={() => setAEliminar(r)}>
            <i className="bi bi-trash3-fill"></i>
          </Button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="container-fluid beneficiarios-modulo" style={{ fontSize: '0.85rem' }}>
      {/* ---------------------------- Indicadores ---------------------------- */}
      <motion.div
        animate={{ opacity: soloTabla ? 0 : 1, height: soloTabla ? 0 : 'auto' }}
        transition={{ duration: 0.4 }}
        style={{ overflow: 'hidden' }}
      >
        {e && (
          <>
            <div className="stat-box d-flex justify-content-between flex-wrap" style={{ gap: '10px' }}>
              <div className="stat-card" style={{ flex: '1 1 180px', padding: '8px' }}>
                <div className="stat-icon"><FaUserFriends /></div>
                <div className="stat-number" style={{ color: '#58c765', fontSize: '1.8rem' }}>{formatoNumero(e.totalBeneficiarios)}</div>
                <h4 style={{ fontSize: '1rem', color: 'gray' }}>Total beneficiarios</h4>
              </div>
              <div className="stat-card" style={{ flex: '1 1 180px', padding: '8px' }}>
                <div className="stat-icon"><FaClipboardList /></div>
                <div className="stat-number" style={{ color: '#185da7', fontSize: '1.8rem' }}>{formatoNumero(e.totalRegistros)}</div>
                <h4 style={{ fontSize: '1rem', color: 'gray' }}>Casos registrados</h4>
              </div>
              <div className="stat-card" style={{ flex: '1 1 240px', padding: 0 }}>
                <ResponsiveContainer width="100%" height={120}>
                  <PieChart>
                    <Pie data={generoPie} dataKey="value" startAngle={180} endAngle={0} cx="50%" cy="70%" outerRadius="70%"
                      label={({ value }) => (e.totalBeneficiarios > 0 ? `${((value * 100) / e.totalBeneficiarios).toFixed(1)}%` : '')}>
                      <Cell fill="#185da7" />
                      <Cell fill="rgba(254, 185, 56, 0.9)" />
                    </Pie>
                    <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: '10px', marginTop: '-15px' }} />
                    <Tooltip formatter={(v) => formatoNumero(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="stat-card" style={{ flex: '1 1 180px', padding: '8px' }}>
                <div className="stat-number" style={{ color: '#6f42c1', fontSize: '1.8rem' }}>{formatoNumero(e.patrocinados.beneficiarios)}</div>
                <h4 style={{ fontSize: '1rem', color: 'gray' }}>Beneficiarios patrocinados</h4>
                <div className="small text-muted">{e.patrocinados.registros} casos</div>
              </div>
              <div className="stat-card" style={{ flex: '1 1 180px', padding: '8px' }}>
                <div className="stat-icon"><FaHandHoldingUsd /></div>
                <div className="stat-number" style={{ color: '#ff7300', fontSize: '1.6rem' }}>$ {formatoNumero(e.montos.totalUsd, 2)}</div>
                <h4 style={{ fontSize: '1rem', color: 'gray' }}>Monto asociado (USD)</h4>
                <div className="small text-muted">
                  {Object.entries(e.montos.porMoneda || {}).map(([m, v]) => `${m} ${formatoNumero(v, 2)}`).join(' · ') || 'Sin pagos'}
                </div>
              </div>
            </div>

            <div className="d-flex gap-2 mt-2 flex-wrap">
              <GraficoBarras titulo="Por grupo de edad" datos={e.porGrupoEtario} clave="descripcion" />
              <GraficoBarras titulo="Por procedencia" datos={e.porProcedencia} />
              <GraficoBarras titulo="Por estado" datos={e.porEstado} />
            </div>
          </>
        )}
      </motion.div>

      {/* ------------------------- Filtros y listado ------------------------- */}
      <div className="row" style={{ marginTop: '10px' }}>
        <div className="col-lg-12">
          <div className="card-box" style={{ padding: '10px' }}>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div>
                <h2 style={{ fontSize: '1.8rem', marginBottom: 0 }}>Beneficiarios sin datos</h2>
                <div className="text-muted small">
                  Casos sin datos individuales. Sume estos totales a los del Cálculo de Indicadores.
                </div>
              </div>
              <div className="d-flex align-items-center">
                <Button variant="info me-2" onClick={() => setSoloTabla((v) => !v)} style={{ padding: '5px 15px' }}
                  title={soloTabla ? 'Mostrar indicadores' : 'Solo tabla'}>
                  {soloTabla ? <FaExpand /> : <FaCompress />}
                </Button>
                <Button variant="info me-2" onClick={() => cargar(pagina)} disabled={recargando} style={{ width: '100px' }} title="Recargar">
                  <FaSync className={recargando ? 'spin' : ''} />
                </Button>
                <Button variant="info me-2" onClick={() => cargar(1)} style={{ width: '100px' }} title="Buscar">
                  <FaSearch />
                </Button>
                <Button variant="btn btn-info me-2" onClick={() => setConfirmarExportar(true)} title="Exportar a Excel">
                  <i className="bi bi-printer-fill"></i>
                </Button>
                <Button variant="btn btn-success" onClick={() => navigate('/promocion/create')}>
                  <i className="bi bi-plus-lg"></i> Nuevo registro
                </Button>
              </div>
            </div>

            <div className="d-flex gap-2 mb-2">
              {/* Sin "Buscar por nombre del caso": el formulario ya no lo pide (26/09/2026). */}
              <Form.Control placeholder="Buscar por nombre de programa" name="nombre_uc" value={filtros.nombre_uc}
                onChange={cambiarFiltro} className={activo(filtros.nombre_uc)}
                onKeyDown={(ev) => ev.key === 'Enter' && cargar(1)} />
              <Form.Control placeholder="Buscar por COD de programa" name="cod_uc" value={filtros.cod_uc}
                onChange={cambiarFiltro} className={activo(filtros.cod_uc)} />
            </div>

            <div className="d-flex gap-2 mb-2 flex-wrap">
              {[
                ['centro_id', 'Centro', opciones.centro, 'id'],
                ['cohorte', 'Cohorte', opciones.cohorte, 'descripcion'],
                ['periodo_id', 'Periodo', opciones.periodo, 'id'],
                ['procedencia_id', 'Procedencia', opciones.procedencia, 'id'],
                ['grupo_prioritario_id', 'Grupo prioritario', opciones.grupo_prioritario, 'id'],
                ['grupo', 'Grupo', opciones.grupo, 'descripcion'],
              ].map(([nombre, etiqueta, lista, valor]) => (
                <Form.Select key={nombre} name={nombre} value={filtros[nombre]} onChange={cambiarFiltro}
                  className={activo(filtros[nombre])} style={{ width: 'auto', flex: '1 1 150px' }}>
                  <option value="">{etiqueta}</option>
                  {(lista || []).map((o) => <option key={o.id} value={o[valor]}>{o.descripcion}</option>)}
                </Form.Select>
              ))}
            </div>

            <div className="d-flex gap-2 mb-2 flex-wrap">
              <Form.Select name="estado_id" value={filtros.estado_id} onChange={cambiarFiltro}
                className={activo(filtros.estado_id)} style={{ width: 'auto', flex: '1 1 160px' }}>
                <option value="">Estado</option>
                {estadosMunicipios.map((es) => <option key={es.id_estado} value={es.id_estado}>{es.estado}</option>)}
              </Form.Select>
              <Form.Select name="municipio" value={filtros.municipio} onChange={cambiarFiltro} disabled={!municipios.length}
                className={activo(filtros.municipio)} style={{ width: 'auto', flex: '1 1 160px' }}>
                <option value="">Municipio</option>
                {municipios.map((m) => <option key={m.municipio} value={m.municipio}>{m.municipio}</option>)}
              </Form.Select>
              <Form.Select name="patrocinante_id" value={filtros.patrocinante_id} onChange={cambiarFiltro}
                className={activo(filtros.patrocinante_id)} style={{ width: 'auto', flex: '2 1 220px' }}>
                <option value="">Patrocinante</option>
                {opciones.patrocinante.map((p) => <option key={p.id} value={p.id}>{p.nombre_patrocinante}</option>)}
              </Form.Select>
              <Form.Select name="es_patrocinado" value={filtros.es_patrocinado} onChange={cambiarFiltro}
                className={activo(filtros.es_patrocinado)} style={{ width: 'auto' }}>
                <option value="">¿Es patrocinado?</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </Form.Select>
              <Form.Select name="realiza_aporte" value={filtros.realiza_aporte} onChange={cambiarFiltro}
                className={activo(filtros.realiza_aporte)} style={{ width: 'auto' }}>
                <option value="">¿Realiza aporte?</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </Form.Select>
            </div>

            <div className="d-flex gap-3 mb-3 align-items-center flex-wrap">
              <div className="d-flex align-items-center">
                <Form.Label className="me-2 mb-0">Fecha mayor que:</Form.Label>
                <Form.Control type="date" name="fecha_mayor" value={filtros.fecha_mayor} onChange={cambiarFiltro} style={{ width: 'auto' }} />
              </div>
              <div className="d-flex align-items-center">
                <Form.Label className="me-2 mb-0">Fecha menor que:</Form.Label>
                <Form.Control type="date" name="fecha_menor" value={filtros.fecha_menor} onChange={cambiarFiltro} style={{ width: 'auto' }} />
              </div>
              <Button variant="link" size="sm" onClick={limpiarFiltros}>Limpiar filtros</Button>
            </div>

            {datos.length === 0 && !recargando ? (
              <div className="text-center text-muted py-4">No hay registros con el filtro aplicado.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <PaginationTable
                  data={datos}
                  itemsPerPage={12}
                  columns={columnas}
                  renderItem={fila}
                  currentPage={pagina}
                  onPageChange={(p) => cargar(p)}
                  totalPages={totalPaginas}
                />
              </div>
            )}

            <ToastContainer />
          </div>
        </div>
      </div>

      <Modal show={!!aEliminar} onHide={() => setAEliminar(null)} centered>
        <Modal.Header closeButton><Modal.Title>Eliminar registro</Modal.Title></Modal.Header>
        <Modal.Body>
          ¿Eliminar el registro de <strong>{aEliminar?.curso?.descripcion || 'sin programa'}</strong> del {String(aEliminar?.fecha_promocion || '').slice(0, 10)} ({formatoNumero(aEliminar?.cantidad_total)} beneficiarios)?
          Esta acción no se puede deshacer.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setAEliminar(null)}>Cancelar</Button>
          <Button variant="danger" onClick={eliminar}>Eliminar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={confirmarExportar} onHide={() => setConfirmarExportar(false)} centered>
        <Modal.Header closeButton><Modal.Title>Exportar a Excel</Modal.Title></Modal.Header>
        <Modal.Body>
          Se descargará un archivo con los registros del filtro aplicado y sus indicadores.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmarExportar(false)}>Cancelar</Button>
          <Button variant="primary" onClick={exportar}>Exportar</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ShowPromocion;
